import axios from 'axios';
import { useAuthStore } from '@/stores/useAuthStore';
import { backend_url } from '@/config/config';

const dartsHubApi = axios.create({
  baseURL: backend_url,
  // 120s : certains outils AdminCleanup parcourent toute la base (ou le registre) côté
  // serveur, plus lentement qu'un appel DartsHub classique -- 60s coupait la requête
  // avant la fin du traitement.
  timeout: 120000,
});

let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: any) => void; reject: (reason?: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

dartsHubApi.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore();
    if (authStore.token) {
      config.headers.Authorization = `Bearer ${authStore.token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

dartsHubApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const authStore = useAuthStore();
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => dartsHubApi(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshed = await authStore.refreshAccessToken();
        if (refreshed) {
          processQueue(null);
          originalRequest.headers.Authorization = `Bearer ${authStore.token}`;
          return dartsHubApi(originalRequest);
        } else {
          throw new Error('Refresh failed');
        }
      } catch (refreshError) {
        processQueue(refreshError);
        authStore.logout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response?.status === 400) {
      console.warn('Bad Request :', error.response.data?.detail || error.response.data);
    }
    if (error.response?.status >= 500) {
      console.error('Erreur serveur :', error.response.data);
    }

    return Promise.reject(error);
  }
);

export default dartsHubApi;
